import React, { Component, useContext } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { filter } from '@atlaskit/adf-utils/traverse';
import type {
	CardAppearance,
	CardDimensions,
	CardOnClickCallback,
	NumericalCardDimensions,
} from '@atlaskit/media-card';
import { Card as CardAsync, CardSync, CardLoading, CardError } from '@atlaskit/media-card';
import type { MediaClientConfig } from '@atlaskit/media-core';
import type {
	ImageResizeMode,
	FileIdentifier,
	ExternalImageIdentifier,
	Identifier,
	FileState,
	MediaClient,
} from '@atlaskit/media-client';
import { MediaClientContext } from '@atlaskit/media-client-react';
import type { MediaType } from '@atlaskit/adf-schema';
import type { ContextIdentifierProvider } from '@atlaskit/editor-common/provider-factory';
import { withImageLoader } from '@atlaskit/editor-common/utils';
import type { ImageStatus } from '@atlaskit/editor-common/utils';
import type { MediaFeatureFlags } from '@atlaskit/media-common';
import type { RendererAppearance } from './Renderer/types';
import type { RendererContext } from '../react/types';
import type { MediaSSR } from '../types/mediaOptions';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import AnalyticsContext from '../analytics/analyticsContext';
import type { AnalyticsEventPayload } from '../analytics/events';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';

export type MediaProvider = {
	viewMediaClientConfig: MediaClientConfig;
};

export interface MediaCardProps {
	alt?: string;
	appearance?: CardAppearance;
	cardDimensions?: CardDimensions;
	collection?: string;
	contextIdentifierProvider?: Promise<ContextIdentifierProvider>;
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dataAttributes?: Record<string, any>;
	disableOverlay?: boolean;
	enableSyncMediaCard?: boolean;
	eventHandlers?: {
		media?: {
			onClick?: CardOnClickCallback;
		};
	};
	featureFlags?: MediaFeatureFlags;
	id?: string;
	imageStatus?: ImageStatus;
	localId?: string;
	occurrenceKey?: string;
	originalDimensions?: NumericalCardDimensions;
	rendererAppearance?: RendererAppearance;
	rendererContext?: RendererContext;
	resizeMode?: ImageResizeMode;
	shouldEnableDownloadButton?: boolean;
	shouldOpenMediaViewer?: boolean;
	ssr?: MediaSSR;
	type: MediaType;
	url?: string;
	useInlinePlayer?: boolean;
}

interface State {
	contextIdentifierProvider?: ContextIdentifierProvider;
	fileState?: FileState;
}

export const mediaIdentifierMap: Map<string, Identifier> = new Map();

export const getListOfIdentifiersFromDoc = (doc?: ADFEntity): Identifier[] => {
	if (!doc) {
		return [];
	}
	return filter(doc, (node) => node.type === 'media').reduce(
		(identifierList: Identifier[], mediaNode) => {
			if (mediaNode.attrs) {
				const { type, url: dataURI, id } = mediaNode.attrs;

				if (type === 'file' && id) {
					identifierList.push({
						mediaItemType: 'file',
						id,
					});
				} else if (type === 'external' && dataURI) {
					identifierList.push({
						mediaItemType: 'external-image',
						dataURI,
						name: dataURI,
					});
				}
			}
			return identifierList;
		},
		[],
	);
};

// Ignored via go/ees005
// eslint-disable-next-line @repo/internal/react/no-class-components
export class MediaCardView extends Component<
	MediaCardProps & {
		fireAnalyticsEvent?: (event: AnalyticsEventPayload) => void;
		mediaClient?: MediaClient;
	},
	State
> {
	state: State = {};

	async componentDidMount(): Promise<void> {
		const {
			rendererContext,
			contextIdentifierProvider,
			id,
			url,
			collection: collectionName,
		} = this.props;

		if (contextIdentifierProvider) {
			this.setState({
				contextIdentifierProvider: await contextIdentifierProvider,
			});
		}

		const nodeIsInCache =
			(id && mediaIdentifierMap.has(id)) || (url && mediaIdentifierMap.has(url));
		if (rendererContext && rendererContext.adDoc && !nodeIsInCache) {
			getListOfIdentifiersFromDoc(rendererContext.adDoc).forEach((identifier) => {
				if (identifier.mediaItemType === 'file' && identifier.id === id) {
					mediaIdentifierMap.set(identifier.id as string, {
						...identifier,
						collectionName,
					});
				} else if (identifier.mediaItemType === 'external-image') {
					mediaIdentifierMap.set(identifier.dataURI as string, identifier);
				}
			});
		}

		if (id) {
			this.saveFileState(id);
		}
	}

	componentDidUpdate(prevProps: MediaCardProps): void {
		const { id: oldId } = prevProps;
		if (this.props.id && oldId !== this.props.id) {
			this.saveFileState(this.props.id);
		}
	}

	componentWillUnmount(): void {
		const { id, url: dataURI } = this.props;

		if (id) {
			mediaIdentifierMap.delete(id);
		} else if (dataURI) {
			mediaIdentifierMap.delete(dataURI);
		}
	}

	saveFileState = async (id: string): Promise<void> => {
		const { collection: collectionName, mediaClient } = this.props;
		const options = {
			collectionName,
		};
		try {
			if (mediaClient) {
				const fileState = await mediaClient.file.getCurrentState(id, options);
				this.setState({
					fileState,
				});
			}
		} catch (error) {
			// do not set state on error
		}
	};

	private onError = (reason: string) => {
		this.props.fireAnalyticsEvent?.({
			action: ACTION.ERRORED,
			actionSubject: ACTION_SUBJECT.RENDERER,
			actionSubjectId: ACTION_SUBJECT_ID.MEDIA,
			eventType: EVENT_TYPE.UI,
			attributes: {
				reason,
				external: false,
			},
		});
	};

	private renderLoadingCard = () => {
		const { cardDimensions } = this.props;

		return (
			<CardLoading dimensions={cardDimensions} interactionName="renderer-media-card-loading" />
		);
	};

	private renderExternal(shouldOpenMediaViewer: boolean) {
		const {
			cardDimensions,
			resizeMode,
			appearance,
			url,
			imageStatus,
			disableOverlay,
			alt,
			featureFlags,
			ssr,
			mediaClient,
			dataAttributes,
			enableSyncMediaCard,
			localId,
		} = this.props;

		if (imageStatus === 'loading' || !url) {
			return this.renderLoadingCard();
		}

		const identifier: ExternalImageIdentifier = {
			dataURI: url,
			name: url,
			mediaItemType: 'external-image',
		};

		// we need this statement for the mandatory mediaClientConfig below
		const mediaClientConfig = mediaClient?.mediaClientConfig;

		const Card = enableSyncMediaCard ? CardSync : CardAsync;

		return (
			// Ignored via go/ees005
			// eslint-disable-next-line react/jsx-props-no-spreading
			<div {...dataAttributes} data-node-type="media" data-local-id={localId}>
				<Card
					// TODO: MPT-315 - clean up after we move mediaClientConfig into FileIdentifier
					// context is not really used when the type is external and we want to render the component asap
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					mediaClientConfig={mediaClientConfig!}
					alt={alt}
					identifier={identifier}
					dimensions={cardDimensions}
					appearance={appearance}
					resizeMode={resizeMode}
					disableOverlay={disableOverlay}
					shouldOpenMediaViewer={shouldOpenMediaViewer}
					mediaViewerItems={Array.from(mediaIdentifierMap.values())}
					featureFlags={featureFlags}
					ssr={ssr?.mode}
					shouldHideTooltip={false}
					onError={
						expValEquals('platform_editor_media_error_analytics', 'isEnabled', true)
							? this.onError
							: undefined
					}
				/>
			</div>
		);
	}

	/**
	 * We want to call provided `eventHandlers.media.onClick` when it's provided,
	 * but we also don't want to call it when it's a video and inline video player is enabled.
	 * This is due to consumers normally process this onClick call by opening media viewer and
	 * we don't want that to happened described above text.
	 */
	private getOnCardClickCallback = (isInlinePlayer: boolean) => {
		const { eventHandlers } = this.props;
		if (eventHandlers && eventHandlers.media && eventHandlers.media.onClick) {
			return ((result, analyticsEvent) => {
				const isVideo = result.mediaItemDetails && result.mediaItemDetails.mediaType === 'video';
				const isVideoWithInlinePlayer = isInlinePlayer && isVideo;
				if (
					!isVideoWithInlinePlayer &&
					eventHandlers &&
					eventHandlers.media &&
					eventHandlers.media.onClick
				) {
					eventHandlers.media.onClick(result, analyticsEvent);
				}
			}) as CardOnClickCallback;
		}

		return undefined;
	};

	render(): React.JSX.Element | null {
		const { contextIdentifierProvider, fileState } = this.state;
		const {
			id,
			alt,
			type,
			collection,
			occurrenceKey,
			cardDimensions,
			resizeMode,
			disableOverlay,
			useInlinePlayer,
			originalDimensions,
			shouldOpenMediaViewer: forceOpenMediaViewer,
			featureFlags,
			shouldEnableDownloadButton,
			ssr,
			mediaClient,
			dataAttributes,
			enableSyncMediaCard,
			localId,
		} = this.props;

		const isMobile = false;
		const shouldPlayInline = useInlinePlayer !== undefined ? useInlinePlayer : true;

		const isInlinePlayer = isMobile ? false : shouldPlayInline;

		const onCardClick = this.getOnCardClickCallback(isInlinePlayer);

		const shouldOpenMediaViewer =
			typeof forceOpenMediaViewer === 'boolean' ? forceOpenMediaViewer : !isMobile && !onCardClick;

		if (type === 'external') {
			return this.renderExternal(shouldOpenMediaViewer);
		}

		if (type === 'link') {
			return null;
		}

		const mediaClientConfig = !!ssr ? ssr.config : mediaClient?.mediaClientConfig;

		if (!mediaClientConfig || !id) {
			return this.renderLoadingCard();
		}

		if (!id || type !== 'file') {
			return <CardError dimensions={cardDimensions} />;
		}
		const contextId = contextIdentifierProvider && contextIdentifierProvider.objectId;

		const identifier: FileIdentifier = {
			id,
			mediaItemType: 'file',
			collectionName: collection,
			occurrenceKey,
		};

		const Card = enableSyncMediaCard ? CardSync : CardAsync;

		// Quick solution to disable lazy loading of images on PDF export pages in Confluence to remedy an issue with images never loading
		// More robust solution will be implemented as part of CCPDF-233 - Link: https://hello.jira.atlassian.cloud/browse/CCPDF-233
		const currentUrl = window.location.href;
		const shouldDisableLazyLoading =
			expValEquals('platform_editor_disable_lazy_load_media', 'isEnabled', true) &&
			currentUrl.includes('/wiki/pdf/spaces/');

		return (
			<div
				// Ignored via go/ees005
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...getClipboardAttrs({
					id,
					alt,
					collection,
					contextIdentifierProvider,
					originalDimensions,
					fileState,
				})}
				// Ignored via go/ees005
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...dataAttributes}
				data-local-id={localId}
			>
				<Card
					identifier={identifier}
					alt={alt}
					contextId={contextId}
					mediaClientConfig={mediaClientConfig}
					dimensions={cardDimensions}
					originalDimensions={originalDimensions}
					onClick={onCardClick}
					resizeMode={resizeMode}
					isLazy={!isMobile && !shouldDisableLazyLoading}
					disableOverlay={disableOverlay}
					useInlinePlayer={isInlinePlayer}
					shouldOpenMediaViewer={shouldOpenMediaViewer}
					mediaViewerItems={Array.from(mediaIdentifierMap.values())}
					featureFlags={featureFlags}
					shouldEnableDownloadButton={shouldEnableDownloadButton}
					ssr={ssr?.mode}
					shouldHideTooltip={isMobile}
					onError={
						expValEquals('platform_editor_media_error_analytics', 'isEnabled', true)
							? this.onError
							: undefined
					}
				/>
			</div>
		);
	}
}

export type ClipboardAttrs = {
	alt?: string;
	collection?: string;
	contextIdentifierProvider?: ContextIdentifierProvider;
	fileState?: FileState;
	id: string;
	originalDimensions?: NumericalCardDimensions;
};

// Needed for copy & paste
export const getClipboardAttrs = ({
	id,
	alt,
	collection,
	contextIdentifierProvider,
	originalDimensions,
	fileState,
}: ClipboardAttrs): { [key: string]: string | number | undefined } => {
	const contextId = contextIdentifierProvider && contextIdentifierProvider.objectId;
	const width = originalDimensions && originalDimensions.width;
	const height = originalDimensions && originalDimensions.height;
	let fileName = 'file'; // default name is needed for Confluence
	let fileSize = 1;
	let fileMimeType = '';

	if (fileState && fileState.status !== 'error') {
		fileSize = fileState.size;
		fileName = fileState.name;
		fileMimeType = fileState.mimeType;
	}

	return {
		'data-context-id': contextId,
		'data-type': 'file',
		'data-node-type': 'media',
		'data-width': width,
		'data-height': height,
		'data-id': id,
		'data-collection': collection,
		'data-file-name': fileName,
		'data-file-size': fileSize,
		'data-file-mime-type': fileMimeType,
		'data-alt': alt,
	};
};

export const MediaCardInternal = (props: MediaCardProps): React.JSX.Element => {
	const mediaClient = useContext(MediaClientContext);

	return (
		<AnalyticsContext.Consumer>
			{({ fireAnalyticsEvent }) => {
				return (
					<MediaCardView
						// Ignored via go/ees005
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...props}
						mediaClient={mediaClient}
						fireAnalyticsEvent={fireAnalyticsEvent}
					/>
				);
			}}
		</AnalyticsContext.Consumer>
	);
};

export const MediaCard = withImageLoader<MediaCardProps>(MediaCardInternal);
